import type {
  AfterContentInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChild,
  inject,
} from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
import { ButtonComponent } from '@freelancer/ui/button';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CardComponent } from '@freelancer/ui/card';
import { IconModule } from '@freelancer/ui/icon';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ListItemPadding } from '@freelancer/ui/list-item';
import { TextSize } from '@freelancer/ui/text';
import { assertNever } from '@freelancer/utils';
import type { Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { SplitButtonOptionComponent } from './split-button-option.component';
import type { SplitButtonOption } from './split-button-option.types';
import { SplitButtonOptionPosition } from './split-button-option.types';
import { SplitButtonPrimaryComponent } from './split-button-primary.component';

type SplitButtonDisplay = 'inline' | 'block';
@Component({
  standalone: true,
  selector: 'fl-split-button',
  imports: [
    ButtonComponent,
    CardComponent,
    IconModule,
    SplitButtonOptionComponent,
    SplitButtonPrimaryComponent,
  ],
  template: `
    <div class="SplitButtonContainer">
      <div class="ButtonGroup">
        <!-- Clean this up once all split button usage is migrated -->
        @if (options?.length > 0) {
          <fl-split-button-primary
            [color]="color"
            [display]="display"
            [displayTablet]="displayTablet"
            [displayDesktop]="displayDesktop"
            [link]="options[0].link"
            [linkQueryParams]="options[0].linkQueryParams"
            [newTab]="options[0].newTab"
            [size]="size"
            [sizeTablet]="sizeTablet"
            [sizeDesktop]="sizeDesktop"
            [busy]="busy"
            [buttonGroupFirst]="options.length > 1"
            (click)="
              $event.stopPropagation(); primaryClicked.emit(); hideOptions()
            "
          >
            <ng-content></ng-content>
          </fl-split-button-primary>
        }
        @if (splitButtonOptions?.length > 0) {
          <ng-content select="fl-split-button-primary"></ng-content>
        }
        <!-- this prevents propagation so clicking it doesn't trigger the click autoclose handler -->
        @if (
          (options?.length > 1 && !busy) ||
          (splitButtonOptions?.length > 0 && !busy)
        ) {
          <fl-button
            class="ChevronButton"
            title="More Options"
            i18n-title="More Options"
            [color]="color"
            [size]="size"
            [sizeTablet]="sizeTablet"
            [sizeDesktop]="sizeDesktop"
            [buttonGroupLast]="true"
            (click)="toggleShouldShowOptions()"
          >
            <fl-icon
              class="ChevronButtonIcon"
              [name]="chevronType"
              [size]="iconSize"
              [sizeTablet]="iconSizeTablet"
              [sizeDesktop]="iconSizeDesktop"
              [color]="chevronColor"
              [useIconFont]="true"
            ></fl-icon>
          </fl-button>
        }
      </div>

      @if (shouldShowOptions) {
        <fl-card
          #dropdown
          class="OptionListCard"
          [border]="'solid'"
          [edgeToEdge]="true"
          [maxContent]="false"
          [attr.data-position]="position"
          (click)="hideOptions()"
        >
          <ul>
            <!-- Clean this up once all split button usage is migrated -->
            @if (dropdownOptions) {
              @for (
                option of dropdownOptions;
                track trackByCopy($index, option)
              ) {
                <fl-split-button-option
                  [displayText]="option.displayText"
                  [link]="option.link"
                  [linkQueryParams]="option.linkQueryParams"
                  [iconName]="option.iconName"
                  [fontSize]="optionFontSize"
                  [fontSizeTablet]="optionFontSizeTablet"
                  [fontSizeDesktop]="optionFontSizeDesktop"
                  [newTab]="option.newTab"
                  [optionPadding]="optionPadding"
                  (click)="
                    $event.stopPropagation();
                    optionSelected.emit(option);
                    hideOptions()
                  "
                ></fl-split-button-option>
              }
            }
            @if (!dropdownOptions) {
              <ng-content select="fl-split-button-option"></ng-content>
            }
          </ul>
        </fl-card>
      }
    </div>
  `,
  styleUrls: ['./split-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SplitButtonComponent
  implements OnChanges, OnInit, AfterContentInit, OnDestroy
{
  private timeUtils = inject(TimeUtils);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);

  iconSize: IconSize;
  iconSizeTablet?: IconSize | undefined;
  iconSizeDesktop?: IconSize | undefined;
  chevronType: 'ui-chevron-down' | 'ui-chevron-up' = 'ui-chevron-down';
  chevronColor: IconColor;
  shouldShowOptions = false;
  position = SplitButtonOptionPosition.BOTTOM;
  dropdownOptions: SplitButtonOption[];
  optionFontSize: TextSize;
  optionFontSizeTablet?: TextSize | undefined;
  optionFontSizeDesktop?: TextSize | undefined;

  get optionPadding(): ListItemPadding {
    switch (this.size) {
      case ButtonSize.MINI:
        return ListItemPadding.XXSMALL;
      case ButtonSize.SMALL:
      case ButtonSize.MID:
        return ListItemPadding.XSMALL;
      case ButtonSize.LARGE:
      case ButtonSize.XLARGE:
      case ButtonSize.XXLARGE:
        return ListItemPadding.SMALL;
      default:
        return assertNever(this.size);
    }
  }

  @Input() color: ButtonColor;
  /** DEPRECATED: Use template-driven options with the split-button-primary and -option components instead */
  @Input() options: readonly SplitButtonOption[];
  @Input() busy: boolean;

  @HostBinding('attr.data-display')
  @Input()
  display: SplitButtonDisplay = 'inline';
  @HostBinding('attr.data-display-tablet')
  @Input()
  displayTablet?: SplitButtonDisplay;
  @HostBinding('attr.data-display-desktop')
  @Input()
  displayDesktop?: SplitButtonDisplay;

  @HostBinding('attr.data-option-font-size')
  @Input()
  size: ButtonSize;

  @HostBinding('attr.data-option-font-size-tablet')
  @Input()
  sizeTablet?: ButtonSize;

  @HostBinding('attr.data-option-font-size-desktop')
  @Input()
  sizeDesktop?: ButtonSize;

  @Output() primaryClicked = new EventEmitter<void>();
  @Output() optionSelected = new EventEmitter<SplitButtonOption>();

  @ViewChild('dropdown', { read: ElementRef })
  dropdown: ElementRef<HTMLElement>;

  @ContentChild(SplitButtonPrimaryComponent)
  splitButtonPrimary: SplitButtonPrimaryComponent;

  @ContentChildren(SplitButtonOptionComponent)
  splitButtonOptions: QueryList<SplitButtonOptionComponent>;

  private splitButtonOptionSubscription?: Subscription;

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent): void {
    if (!event || !this.shouldShowOptions || !event.target) {
      return;
    }
    const hasClickedInsideComponent = this.elementRef.nativeElement.contains(
      event.target as Node,
    );
    if (!hasClickedInsideComponent) {
      this.hideOptions();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // reapply whenever style inputs change
    this.applyStylesToPrimaryBtn();
    this.applyStylesToOptions();

    if ('options' in changes && this.options) {
      this.dropdownOptions = this.options.slice(1);
    }

    if (this.splitButtonPrimary && this.primaryClicked.observed) {
      throw new Error(
        'Template driven split buttons do not support (primaryClicked) and (optionSelected) outputs. Use click handlers on child components instead',
      );
    }

    if ('size' in changes) {
      this.getFontSize();
      this.getIconSize();
    }
  }

  ngOnInit(): void {
    this.getFontSize();
    this.getIconSize();
  }

  ngAfterContentInit(): void {
    this.applyStylesToPrimaryBtn();

    // re-apply styles whenever the list of options changes
    this.splitButtonOptionSubscription = this.splitButtonOptions.changes
      .pipe(startWith(this.splitButtonOptions.toArray()))
      .subscribe(() => this.applyStylesToOptions());
  }

  applyStylesToPrimaryBtn(): void {
    if (this.splitButtonPrimary) {
      const primaryButton = this.splitButtonPrimary;

      primaryButton.changeDetectorRef.detach();

      primaryButton.busy = this.busy;
      primaryButton.size = this.size;
      primaryButton.display = this.display;
      primaryButton.displayTablet = this.displayTablet;
      primaryButton.displayDesktop = this.displayDesktop;
      primaryButton.color = this.color;
      primaryButton.buttonGroupFirst = this.splitButtonOptions.length > 0;

      primaryButton.changeDetectorRef.reattach();
      primaryButton.changeDetectorRef.detectChanges();
    }
  }

  generateIconSize(size: ButtonSize | undefined): IconSize | undefined {
    switch (size) {
      case ButtonSize.MINI:
      case ButtonSize.SMALL:
      case ButtonSize.MID:
        return IconSize.SMALL;
      case ButtonSize.LARGE:
      case ButtonSize.XLARGE:
      case ButtonSize.XXLARGE:
        return IconSize.MID;
      default:
        return size;
    }
  }

  getIconSize(): void {
    this.iconSize = this.generateIconSize(this.size) || IconSize.MID;
    this.iconSizeTablet = this.generateIconSize(this.sizeTablet);
    this.iconSizeDesktop = this.generateIconSize(this.sizeDesktop);
  }

  generateFontSize(size: ButtonSize | undefined): TextSize | undefined {
    switch (size) {
      case ButtonSize.MINI:
        return TextSize.BODY_16;
      case ButtonSize.SMALL:
      case ButtonSize.MID:
        return TextSize.BODY_16;
      case ButtonSize.LARGE:
      case ButtonSize.XLARGE:
      case ButtonSize.XXLARGE:
        return TextSize.BODY_16;
      default:
        return size;
    }
  }

  getFontSize(): void {
    this.optionFontSize = this.generateFontSize(this.size) || TextSize.BODY_16;
    this.optionFontSizeTablet = this.generateFontSize(this.sizeTablet);
    this.optionFontSizeDesktop = this.generateFontSize(this.sizeDesktop);
  }

  applyStylesToOptions(): void {
    if (this.splitButtonOptions) {
      this.chevronColor = [
        ButtonColor.DEFAULT,
        ButtonColor.TRANSPARENT_DARK,
      ].includes(this.color)
        ? IconColor.FOREGROUND
        : IconColor.LIGHT;

      this.splitButtonOptions.forEach(component => {
        // Use the dedicated API instead of fighting Angular's lifecycle
        component.applyStyles({
          fontSize: this.optionFontSize,
          fontSizeTablet: this.optionFontSizeTablet,
          fontSizeDesktop: this.optionFontSizeDesktop,
          optionPadding: this.optionPadding,
          iconSize: this.iconSize,
        });
      });
    }
  }

  hideOptions(): void {
    this.shouldShowOptions = false;
    this.updateChevron();
    this.setDropdownPosition();
  }

  toggleShouldShowOptions(): void {
    this.shouldShowOptions = !this.shouldShowOptions;
    this.updateChevron();

    // Needs setTimeout so getBoundingClientRect returns the right values
    this.timeUtils.setTimeout(() => {
      this.setDropdownPosition();
      this.changeDetectorRef.markForCheck();
    });
  }

  updateChevron(): void {
    this.chevronType = this.shouldShowOptions
      ? 'ui-chevron-up'
      : 'ui-chevron-down';
  }

  setDropdownPosition(): void {
    if (!this.dropdown) {
      this.position = SplitButtonOptionPosition.BOTTOM;
      this.changeDetectorRef.markForCheck();
      return;
    }

    if (this.shouldShowOptions) {
      const dropdownPosition =
        this.dropdown.nativeElement.getBoundingClientRect().top +
        this.dropdown.nativeElement.getBoundingClientRect().height;

      this.position =
        dropdownPosition > window.innerHeight
          ? SplitButtonOptionPosition.TOP
          : SplitButtonOptionPosition.BOTTOM;

      this.changeDetectorRef.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.splitButtonOptionSubscription?.unsubscribe();
  }

  trackByCopy(_: number, option: SplitButtonOption): string {
    return option.displayText;
  }
}
